import {
  FIELD_TYPE,
  REVIEW_ACTION,
  REVIEW_STATUS,
} from "v2/constants/extractionReview";
import type {
  ReviewActionFn,
  ReviewField,
  ReviewFieldAction,
} from "v2/types/extractionReview.types";
import { renderTypedValue } from "v2/utils/renderTypedValue";
import { ConfirmRejectButtons } from "./ConfirmRejectButtons";
import { ConflictRow } from "./ConflictRow";
import { FieldLabel } from "./FieldLabel";
import { KeyValueList } from "./KeyValueList";
import { StatusBadge } from "./StatusBadge";
import { StruckValue } from "./StruckValue";
import { UndoButton } from "./UndoButton";

type FieldRowProps = {
  field: ReviewField;
  onAction: ReviewActionFn;
  pendingAction?: ReviewFieldAction | null;
};

export const FieldRow = ({
  field,
  onAction,
  pendingAction = null,
}: FieldRowProps) => {
  const { field_id: id, label, status, type } = field;
  const isObject = type === FIELD_TYPE.OBJECT;

  if (status === REVIEW_STATUS.CONFLICT) {
    return (
      <ConflictRow
        field={field}
        onAction={onAction}
        pendingAction={pendingAction}
      />
    );
  }

  const isConfirmed = status === REVIEW_STATUS.CONFIRMED;
  const isRejected = status === REVIEW_STATUS.REJECTED;
  const isSaved = status === REVIEW_STATUS.SAVED;

  let actions: React.ReactNode = (
    <ConfirmRejectButtons
      id={id}
      onAction={onAction}
      pendingAction={pendingAction}
    />
  );
  if (isSaved) {
    actions = null;
  } else if (isConfirmed || isRejected) {
    actions = (
      <UndoButton
        id={id}
        onAction={onAction}
        loading={pendingAction === REVIEW_ACTION.UNDO}
      />
    );
  }

  return (
    <div className="t-flex t-items-center t-justify-between t-gap-4">
      <div className="t-flex t-min-w-0 t-flex-col t-gap-0.5">
        <FieldLabel>{label}</FieldLabel>
        <div className="t-flex t-flex-wrap t-items-center t-gap-2">
          <StruckValue struck={isRejected}>
            {isObject ? (
              <KeyValueList value={field.value} labels={field.value_labels} />
            ) : (
              renderTypedValue(field.value, type)
            )}
          </StruckValue>
          <StatusBadge status={status} />
        </div>
      </div>
      <div className="t-shrink-0">{actions}</div>
    </div>
  );
};
